import { z } from 'zod';

/**
 * Outgoing mail (SMTP) settings, editable from Settings → Notifications.
 *
 * Same override model as the AI provider: `.env` supplies the defaults, a
 * saved row wins, and any field left blank falls back to the `.env` value.
 */

/** What the Settings page reads. The password is never sent back — only whether one is set. */
export interface SmtpSettings {
  host: string;
  port: number;
  /** Implicit TLS (usually port 465). Off means plain + STARTTLS when offered (587/25). */
  secure: boolean;
  username: string;
  hasPassword: boolean;
  from: string;
  updatedAt: number | null;
}

export const smtpSettingsUpdateSchema = z.object({
  host: z.string().trim().optional(),
  port: z.coerce.number().int().min(1).max(65535).optional(),
  secure: z.boolean().optional(),
  username: z.string().trim().optional(),
  // Omitted = keep the stored password. Empty string is rejected — use
  // clearPassword — so a blank field can't wipe a working password.
  password: z.string().min(1).optional(),
  clearPassword: z.boolean().optional(),
  from: z.string().trim().optional(),
});
export type SmtpSettingsUpdate = z.infer<typeof smtpSettingsUpdateSchema>;

/** Test the (possibly unsaved) form values: verify the relay, then send one message. */
export const smtpTestSchema = smtpSettingsUpdateSchema.extend({
  to: z.string().email(),
});
export type SmtpTestRequest = z.infer<typeof smtpTestSchema>;

export interface SmtpTestResult {
  ok: boolean;
  /** Which step failed, so "wrong password" and "port blocked" read differently. */
  stage?: 'connect' | 'send';
  detail: string;
}
